<template>
  <div class="ppm-table-wrap" ref="tableWrap" @scroll="onScroll">
    <table class="ppm-table" ref="table">
      <thead>
        <!-- Строка 1: Месяцы -->
        <tr class="thead-row-months">
          <th
            rowspan="2"
            class="th-node sticky-col"
            :style="{
              width: colWidths.node + 'px',
              minWidth: colWidths.node + 'px',
              maxWidth: colWidths.node + 'px',
            }"
          >
            <div class="th-node__inner">
              <q-icon name="account_tree" size="12px" class="th-node__icon" />
              <span>Наименование</span>
            </div>
            <div
              class="col-resize-handle"
              @mousedown.prevent="$emit('start-col-resize', $event, 'node')"
            />
          </th>
          <th
            rowspan="2"
            class="th-amount sticky-col sticky-col--amount"
            :style="{
              left: colWidths.node + 'px',
              width: colWidths.amount + 'px',
              minWidth: colWidths.amount + 'px',
              maxWidth: colWidths.amount + 'px',
            }"
          >
            Объем<br />Ед. изм.
            <div
              class="col-resize-handle"
              @mousedown.prevent="$emit('start-col-resize', $event, 'amount')"
            />
          </th>
          <th
            rowspan="2"
            class="th-amount th-fact sticky-col sticky-col--fact"
            :style="{
              left: colWidths.node + colWidths.amount + 'px',
              width: colWidths.fact + 'px',
              minWidth: colWidths.fact + 'px',
              maxWidth: colWidths.fact + 'px',
            }"
          >
            Факт<br />%
            <div
              class="col-resize-handle"
              @mousedown.prevent="$emit('start-col-resize', $event, 'fact')"
            />
          </th>
          <template v-if="calendarDates.length">
            <th
              v-for="mg in monthGroups"
              :key="mg.key"
              :colspan="mg.count"
              class="th-month"
            >
              <span
                class="th-month__label"
                :style="{
                  left: colWidths.node + colWidths.amount + colWidths.fact + 8 + 'px',
                  right: '8px',
                }"
              >{{ mg.label }}</span>
            </th>
          </template>
          <th v-else colspan="1" class="th-month th-empty-period">Период не задан</th>
        </tr>
        <!-- Строка 2: Числа + день недели -->
        <tr class="thead-row-days">
          <template v-if="calendarDates.length">
            <th
              v-for="d in calendarDates"
              :key="d.key"
              class="th-day"
              :class="{
                'th-day--weekend': d.isWeekend,
                'th-day--today': d.iso === todayIso,
              }"
              style="width: 80px; min-width: 72px"
            >
              <div class="day-num">{{ d.day }}</div>
              <div class="day-wd">{{ d.wd }}</div>
              <div class="day-unit">₸</div>
            </th>
          </template>
          <th v-else class="th-day">—</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-if="vTopSpacerHeight > 0"
          :style="{ height: vTopSpacerHeight + 'px' }"
          aria-hidden="true"
        >
          <td :colspan="3 + Math.max(1, calendarDates.length)" />
        </tr>

        <PpmTableRow
          v-for="row in slicedRows"
          :key="row.id"
          :row="row"
          :calendar-dates="calendarDates"
          :col-widths="colWidths"
          :expanded-set="expandedSet"
          :today-iso="todayIso"
          :is-future-date="isFutureDate"
          :show-full-names="showFullNames"
          :hierarchy-pct-map="hierarchyPctMap"
          :group-completion-map="groupCompletionMap"
          :parent-cell-data-map="parentCellDataMap"
          :floor-loading-ids="floorLoadingIds"
          :block-loading-ids="blockLoadingIds"
          :gpr-loading-guids="gprLoadingGuids"
          :gpr-periods-by-name="gprPeriodsByName"
          :completed-works="completedWorks"
          :cell-values-row="cellValues[row.id] || {}"
          :cell-meta-row="cellMeta[row.id] || {}"
          :cumulative-fact-sum="cumulativeFactSums[row.id] || 0"
          :cumulative-fact-pct="workFactPctMap[row.id] || 0"
          :smeta-change-log="smetaChangeLog"
          :storno-mode="stornoMode"
          @toggle-expand="$emit('toggle-expand', $event)"
          @open-fact-dialog="(row, d) => $emit('open-fact-dialog', row, d)"
          @open-group-fact-dialog="(row, d) => $emit('open-group-fact-dialog', row, d)"
          @open-fact-history="(row) => $emit('open-fact-history', row)"
          @complete-work="(rows) => $emit('complete-work', rows)"
          @uncomplete-work="(row) => $emit('uncomplete-work', row)"
          @delete-fact="(row, d) => $emit('delete-fact', row, d)"
        />

        <tr
          v-if="vBottomSpacerHeight > 0"
          :style="{ height: vBottomSpacerHeight + 'px' }"
          aria-hidden="true"
        >
          <td :colspan="3 + Math.max(1, calendarDates.length)" />
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import PpmTableRow from "./PpmTableRow.vue";

const V_ROW_HEIGHT = 44;
const V_OVERSCAN = 15;

export default {
  name: "PpmTable",
  components: { PpmTableRow },

  props: {
    visibleRows:       { type: Array, required: true },
    calendarDates:     { type: Array, required: true },
    monthGroups:       { type: Array, required: true },
    colWidths:         { type: Object, required: true },
    expandedSet:       { type: Set, required: true },
    todayIso:          { type: String, default: "" },
    isFutureDate:      { type: Boolean, default: false },
    showFullNames:     { type: Boolean, default: false },
    hierarchyPctMap:   { type: Object, default: () => ({}) },
    groupCompletionMap:{ type: Object, default: () => ({}) },
    parentCellDataMap: { type: Object, default: () => ({}) },
    floorLoadingIds:   { type: Object, default: () => ({}) },
    blockLoadingIds:   { type: Object, default: () => ({}) },
    gprLoadingGuids:   { type: Object, default: () => ({}) },
    completedWorks:    { type: Set, default: () => new Set() },
    cellValues:        { type: Object, default: () => ({}) },
    cellMeta:          { type: Object, default: () => ({}) },
    cumulativeFactSums:{ type: Object, default: () => ({}) },
    workFactPctMap:    { type: Object, default: () => ({}) },
    gprPeriodsByName:  { type: Object, default: () => ({}) },
    smetaChangeLog:    { type: Object, default: () => ({}) },
    stornoMode:        { type: Boolean, default: false },
  },

  emits: [
    "toggle-expand",
    "open-fact-dialog",
    "open-group-fact-dialog",
    "open-fact-history",
    "complete-work",
    "uncomplete-work",
    "start-col-resize",
    "delete-fact",
  ],

  data() {
    return {
      vStartIdx: 0,
      vEndIdx: 30,
      vViewportH: 800,
    };
  },

  created() {
    this._scrollRaf = null;
    this._rawScrollTop = 0;
  },

  beforeUnmount() {
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
  },

  watch: {
    visibleRows() {
      this._recalcRange(this._rawScrollTop);
    },
  },

  computed: {
    slicedRows() {
      const start = this.vStartIdx;
      const end = Math.min(this.vEndIdx, this.visibleRows.length);
      const slice = this.visibleRows.slice(start, end);
      for (let i = 0; i < slice.length; i++) {
        slice[i]._vIdx = start + i;
      }
      return slice;
    },
    vTopSpacerHeight() {
      return this.vStartIdx * V_ROW_HEIGHT;
    },
    vBottomSpacerHeight() {
      return Math.max(0, (this.visibleRows.length - this.vEndIdx) * V_ROW_HEIGHT);
    },
  },

  methods: {
    _recalcRange(scrollTop) {
      const vh = this.vViewportH || 800;
      const total = this.visibleRows.length;
      const virtualMax = Math.max(0, total * V_ROW_HEIGHT - vh);
      const st = Math.min(scrollTop, virtualMax);

      const newStart = Math.max(0, Math.floor(st / V_ROW_HEIGHT) - V_OVERSCAN);
      const newEnd = Math.min(total, Math.ceil((st + vh) / V_ROW_HEIGHT) + V_OVERSCAN);
      if (newStart !== this.vStartIdx || newEnd !== this.vEndIdx) {
        this.vStartIdx = newStart;
        this.vEndIdx = newEnd;
      }
    },

    onScroll(e) {
      const el = e.target;
      this._rawScrollTop = el.scrollTop;
      const h = el.clientHeight;
      if (h && h !== this.vViewportH) this.vViewportH = h;

      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        const st = this.$refs.tableWrap?.scrollTop ?? this._rawScrollTop;
        this._recalcRange(st);
      });
    },

    resetScroll() {
      this._rawScrollTop = 0;
      this.vStartIdx = 0;
      this.vEndIdx = Math.min(30, this.visibleRows.length);
      if (this.$refs.tableWrap) this.$refs.tableWrap.scrollTop = 0;
    },
  },
};
</script>
